import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Card, CardBody } from "../../components/ui";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheckCircle,
  FaExclamationTriangle,
  FaClock,
  FaCode,
  FaFilter,
  FaGlobe,
  FaPause,
  FaRobot,
  FaMicrophone,
  FaPaperPlane,
  FaSyncAlt,
  FaHistory,
  FaLightbulb,
  FaChevronRight,
} from "react-icons/fa";
import { FaCodeBranch, FaBolt, FaCommentDots } from "react-icons/fa";

export default function FlowBuilderGuide() {
  useEffect(() => {
    document.title = "Flow Builder Guide | Walytic";
  }, []);

  return (
    <div className="page-content">
      <PageHeader title="Flow Builder Guide" subtitle="Build automated WhatsApp workflows - from simple auto-replies to full cold outreach campaigns." />

      <div className="max-w-4xl mx-auto">
        {/* Table of Contents */}
        <nav className="bg-surface-card rounded-xl border border-surface-border p-6 mb-12">
          <h2 className="font-semibold text-lg text-ink-primary mb-4">In this guide</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              {
                id: "what-is-flow-builder",
                label: "What is Flow Builder?",
              },
              { id: "create-first-flow", label: "Create your first flow" },
              { id: "all-node-types", label: "All node types explained" },
              { id: "cold-outreach", label: "Cold outreach campaigns" },
              { id: "auto-reply-bot", label: "Build an auto-reply bot" },
              { id: "variables", label: "Using variables in messages" },
              {
                id: "execution-history",
                label: "Checking execution history",
              },
              { id: "tips", label: "Tips and best practices" },
            ].map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="flex items-center text-sm text-ink-secondary hover:text-brand py-1.5 transition-colors"
              >
                <FaChevronRight className="h-4 w-4 mr-1 text-ink-muted" />
                {item.label}
              </a>
            ))}
          </div>
        </nav>

        {/* Section: What is Flow Builder */}
        <section id="what-is-flow-builder" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">
            What is Flow Builder?
          </h2>
          <p className="text-ink-secondary mb-4 leading-relaxed">
            Flow Builder is a visual drag-and-drop editor where you create
            automated WhatsApp workflows. Think of it as a flowchart: you
            connect different actions together, and Walytic runs them
            automatically when something happens (like receiving a message)
            or on a schedule.
          </p>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Common things people build with Flow Builder:
          </p>
          <ul className="space-y-3 mb-6">
            {[
              "Auto-reply bots that answer customer questions 24/7",
              "Cold outreach campaigns that send messages to a list on a schedule",
              "Welcome sequences for new contacts",
              "AI-powered chatbots that use your business knowledge to reply",
              "Webhook integrations that connect WhatsApp to your other tools",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-ink-secondary">
                <FaCheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Section: Create First Flow */}
        <section id="create-first-flow" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">
            Create your first flow
          </h2>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">
                1
              </div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">
                  Go to Flow Builder
                </h3>
                <p className="text-ink-secondary">
                  In the left sidebar, click{" "}
                  <strong>Flow Builder</strong>. You will see a list of all
                  your flows (empty at first).
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">
                2
              </div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">
                  Click &quot;New Flow&quot;
                </h3>
                <p className="text-ink-secondary mb-3">
                  Give your flow a name (e.g., &quot;Welcome Bot&quot; or
                  &quot;Cold Outreach Campaign&quot;) and pick the WhatsApp
                  session you want it to run on. Only connected sessions
                  show in the dropdown.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <FaExclamationTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      Make sure your WhatsApp session is connected before
                      creating a flow. Go to{" "}
                      <strong>WhatsApp Phones</strong> and scan the QR code
                      if it is not connected.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">
                3
              </div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">
                  The canvas opens
                </h3>
                <p className="text-ink-secondary mb-3">
                  You will see a blank canvas with one green &quot;Message
                  Received&quot; node already placed. This is your starting
                  point. At the top, there is a toolbar with all the node
                  types you can add.
                </p>
                <p className="text-ink-secondary">
                  To add a node: click a node type in the toolbar, then
                  click on the canvas to place it. Or drag it from the
                  toolbar onto the canvas.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">
                4
              </div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">
                  Connect nodes together
                </h3>
                <p className="text-ink-secondary mb-3">
                  Drag from the small circle on the bottom of one node to
                  the top of another node. This creates a connection (edge)
                  that tells Walytic the order to execute actions.
                </p>
                <p className="text-ink-secondary">
                  Click any node to open its settings panel on the right
                  side. Configure each node, then click{" "}
                  <strong>Save Flow</strong> in the top toolbar.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">
                5
              </div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">
                  Activate the flow
                </h3>
                <p className="text-ink-secondary">
                  Go back to the flow list and toggle the switch to{" "}
                  <strong>Active</strong>. Your flow is now live. When a
                  message matches your trigger (or the loop schedule fires),
                  Walytic runs the flow automatically.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section: All Node Types */}
        <section id="all-node-types" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-6">
            All node types explained
          </h2>
          <p className="text-ink-secondary mb-8">
            Flow Builder has 11 node types. Here is what each one does and
            how to configure it.
          </p>

          <div className="space-y-6">
            {/* Message Received */}
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <FaBolt className="h-4 w-4 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">
                  Message Received (Trigger)
                </h3>
                <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                  Entry point
                </span>
              </div>
              <p className="text-ink-secondary mb-3">
                This is the starting point for most flows. It listens for
                incoming WhatsApp messages and starts the flow when a
                message matches your keyword.
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-ink-primary mb-2">
                  Settings:
                </p>
                <ul className="text-sm text-ink-secondary space-y-1">
                  <li>
                    <strong>Match Type:</strong> Contains, Exact Match,
                    Starts With, or Regex
                  </li>
                  <li>
                    <strong>Keyword:</strong> The word or phrase to listen
                    for (e.g., &quot;hi&quot;, &quot;pricing&quot;,
                    &quot;help&quot;)
                  </li>
                </ul>
              </div>
              <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Example:</strong> Set Match Type to
                  &quot;Contains&quot; and Keyword to &quot;price&quot;. Now
                  anyone who sends a message containing the word
                  &quot;price&quot; will trigger this flow.
                </p>
              </div>
            </div>

            {/* Send Message */}
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FaPaperPlane className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Send Message</h3>
              </div>
              <p className="text-ink-secondary mb-3">
                Sends a WhatsApp message to the contact. You can include
                text, images, videos, or documents.
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-ink-primary mb-2">
                  Settings:
                </p>
                <ul className="text-sm text-ink-secondary space-y-1">
                  <li>
                    <strong>Message:</strong> The text to send. Supports
                    WhatsApp formatting: *bold*, _italic_
                  </li>
                  <li>
                    <strong>Media URL:</strong> (Optional) Link to an image,
                    video, or document to attach
                  </li>
                </ul>
              </div>
              <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Use variables like{" "}
                  {"{{firstName}}"} to personalize messages. See the{" "}
                  <a href="#variables" className="underline">
                    variables section
                  </a>{" "}
                  below.
                </p>
              </div>
            </div>

            {/* Delay */}
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <FaClock className="h-4 w-4 text-amber-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Delay</h3>
              </div>
              <p className="text-ink-secondary mb-3">
                Pauses the flow for a set amount of time before moving to
                the next node.
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-ink-primary mb-2">
                  Settings:
                </p>
                <ul className="text-sm text-ink-secondary space-y-1">
                  <li>
                    <strong>Duration:</strong> A number (e.g., 5, 30, 2)
                  </li>
                  <li>
                    <strong>Unit:</strong> Seconds, Minutes, Hours, or Days
                  </li>
                </ul>
              </div>
              <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Example:</strong> Set Duration to 30 and Unit to
                  &quot;Minutes&quot; to wait 30 minutes between messages.
                  Good for making conversations feel natural.
                </p>
              </div>
            </div>

            {/* Condition */}
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <FaFilter className="h-4 w-4 text-purple-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Condition</h3>
              </div>
              <p className="text-ink-secondary mb-3">
                Splits the flow into two paths based on whether the
                contact&apos;s message matches a keyword. The YES path runs
                if it matches; the NO path runs if it does not.
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-ink-primary mb-2">
                  Settings:
                </p>
                <ul className="text-sm text-ink-secondary space-y-1">
                  <li>
                    <strong>Match Type:</strong> Contains, Exact Match,
                    Starts With, or Regex
                  </li>
                  <li>
                    <strong>Keyword:</strong> The word to check for
                  </li>
                </ul>
              </div>
              <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Example:</strong> After asking &quot;Are you
                  interested? Reply YES or NO&quot;, add a Condition node
                  with keyword &quot;yes&quot;. The YES path sends pricing
                  info, the NO path sends a polite goodbye.
                </p>
              </div>
            </div>

            {/* Wait for Reply */}
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <FaPause className="h-4 w-4 text-orange-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Wait for Reply</h3>
              </div>
              <p className="text-ink-secondary mb-3">
                Pauses the flow until the contact responds &mdash; or until
                a timeout you choose elapses. Two output handles let you
                branch the flow based on which happens first:
              </p>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2 text-sm text-ink-secondary">
                  <span className="inline-block h-2 w-2 rounded-full bg-orange-500 mt-1.5 flex-shrink-0"></span>
                  <span><strong>Replied</strong> &mdash; fires the moment the contact sends any message. Connect this to your follow-up logic.</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-ink-secondary">
                  <span className="inline-block h-2 w-2 rounded-full bg-amber-600 mt-1.5 flex-shrink-0"></span>
                  <span><strong>Timeout</strong> &mdash; fires if no reply arrives within the window you set (minutes, hours, or days). Connect this to a check-in message, an internal alert, or a different branch entirely.</span>
                </li>
              </ul>
              <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Example:</strong> Send Message 1 &rarr; Wait for Reply (timeout 2 hours) &rarr; on <strong>Replied</strong> end the flow, on <strong>Timeout</strong> send Message 2 (&ldquo;Just checking in&hellip;&rdquo;). The follow-up only fires if the contact actually went silent.
                </p>
              </div>
              <div className="mt-3 bg-amber-50 border border-amber-100 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Tip:</strong> Pair the <strong>Replied</strong> branch with a Condition node to also check <em>what</em> the contact said &mdash; e.g., YES vs NO routing.
                </p>
              </div>
            </div>

            {/* Send to AI */}
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <FaRobot className="h-4 w-4 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Send to AI</h3>
              </div>
              <p className="text-ink-secondary mb-3">
                Sends the contact&apos;s message to OpenAI (ChatGPT) and
                replies with the AI&apos;s response. You can give the AI
                instructions and attach your knowledge bases so it answers
                accurately.
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-ink-primary mb-2">
                  Settings:
                </p>
                <ul className="text-sm text-ink-secondary space-y-1">
                  <li>
                    <strong>System Prompt:</strong> Instructions for the AI
                    (e.g., &quot;You are a helpful sales assistant for
                    [company]. Answer questions about our products.&quot;)
                  </li>
                  <li>
                    <strong>Knowledge Bases:</strong> Select which knowledge
                    bases to use (set up in Settings &gt; AI)
                  </li>
                  <li>
                    <strong>Silent Mode:</strong> If on, the AI response is
                    not sent to the contact -- it is only passed to the next
                    node
                  </li>
                </ul>
              </div>
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Requires:</strong> An OpenAI API key in{" "}
                  <Link to="/guide/ai-features" className="underline">
                    Settings &gt; AI
                  </Link>
                  .
                </p>
              </div>
            </div>

            {/* AI Conversation */}
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <FaCommentDots className="h-4 w-4 text-violet-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">AI Conversation</h3>
              </div>
              <p className="text-ink-secondary mb-3">
                Like &quot;Send to AI&quot; but keeps a multi-turn
                conversation going. The AI remembers context from previous
                messages. The conversation continues until the contact types
                an exit keyword or reaches the max number of turns.
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-ink-primary mb-2">
                  Settings:
                </p>
                <ul className="text-sm text-ink-secondary space-y-1">
                  <li>
                    <strong>System Prompt:</strong> Instructions for the AI
                  </li>
                  <li>
                    <strong>Knowledge Bases:</strong> Select which to use
                  </li>
                  <li>
                    <strong>Exit Keyword:</strong> Contact types this to end
                    the AI chat (default: &quot;stop&quot;)
                  </li>
                  <li>
                    <strong>Max Turns:</strong> Maximum back-and-forth
                    messages (0 = unlimited)
                  </li>
                </ul>
              </div>
            </div>

            {/* Loop */}
            <div
              id="loop-node"
              className="bg-surface-card rounded-xl border-2 border-teal-300 p-6 scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-teal-100 flex items-center justify-center">
                  <FaSyncAlt className="h-4 w-4 text-teal-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Loop</h3>
                <span className="text-xs px-2 py-0.5 rounded bg-teal-100 text-teal-700">
                  Key for cold outreach
                </span>
              </div>
              <p className="text-ink-secondary mb-3">
                The Loop node sends a message to a list of phone numbers on
                a schedule. This is the node you use for cold outreach, bulk
                campaigns, and scheduled broadcasts.
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-ink-primary mb-2">
                  Settings:
                </p>
                <ul className="text-sm text-ink-secondary space-y-1">
                  <li>
                    <strong>Message:</strong> The text to send to each
                    recipient
                  </li>
                  <li>
                    <strong>Media URL:</strong> (Optional) Image, video, or
                    document to attach
                  </li>
                  <li>
                    <strong>Recipients:</strong> Paste phone numbers (one
                    per line) or upload a CSV file. Max 1,000 numbers.
                  </li>
                  <li>
                    <strong>Repeat Count:</strong> How many times to run the
                    loop (0 = keep going forever, max 10,000)
                  </li>
                  <li>
                    <strong>Interval Type:</strong> Simple (every X
                    minutes/hours) or Cron (advanced scheduling)
                  </li>
                  <li>
                    <strong>Interval:</strong> For Simple: a number + unit
                    (e.g., 30 minutes). For Cron: a cron expression.
                  </li>
                </ul>
              </div>
              <div className="mt-3 bg-teal-50 border border-teal-200 rounded-lg p-3">
                <p className="text-sm text-teal-800">
                  <strong>This is what you need for cold messaging.</strong>{" "}
                  See the detailed walkthrough in the{" "}
                  <a
                    href="#cold-outreach"
                    className="underline font-medium"
                  >
                    Cold Outreach section
                  </a>{" "}
                  below.
                </p>
              </div>
            </div>

            {/* Trigger Webhook */}
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <FaGlobe className="h-4 w-4 text-red-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Trigger Webhook</h3>
              </div>
              <p className="text-ink-secondary mb-3">
                Sends data to an external URL (HTTP POST). Use this to
                connect your flow to Zapier, Make, Google Sheets, your CRM,
                or any other tool that accepts webhooks.
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-ink-primary mb-2">
                  Settings:
                </p>
                <ul className="text-sm text-ink-secondary space-y-1">
                  <li>
                    <strong>Webhook URL:</strong> The URL to send data to
                  </li>
                  <li>
                    <strong>Custom Headers:</strong> (Optional) Extra
                    headers as JSON
                  </li>
                </ul>
                <p className="text-sm text-ink-muted mt-2">
                  Automatically sends: contact name, phone number, message
                  text, timestamp, session ID, and flow name.
                </p>
              </div>
            </div>

            {/* Voice Note */}
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-pink-100 flex items-center justify-center">
                  <FaMicrophone className="h-4 w-4 text-pink-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">Voice Note</h3>
              </div>
              <p className="text-ink-secondary mb-3">
                Converts text to speech and sends it as a WhatsApp voice
                note. Choose between ElevenLabs (high quality, natural
                voice) or OpenAI TTS.
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-ink-primary mb-2">
                  Settings:
                </p>
                <ul className="text-sm text-ink-secondary space-y-1">
                  <li>
                    <strong>Text to Speak:</strong> The text that will be
                    converted to audio
                  </li>
                  <li>
                    <strong>Provider:</strong> ElevenLabs or OpenAI TTS
                  </li>
                  <li>
                    <strong>Voice:</strong> Pick a voice (ElevenLabs: Voice
                    ID, OpenAI: Alloy, Echo, Fable, Onyx, Nova, Shimmer)
                  </li>
                </ul>
              </div>
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Requires:</strong> An ElevenLabs or OpenAI API key
                  in{" "}
                  <Link to="/guide/ai-features" className="underline">
                    Settings &gt; AI
                  </Link>
                  .
                </p>
              </div>
            </div>

            {/* HTTP Request */}
            <div className="bg-surface-card rounded-xl border border-surface-border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg bg-cyan-100 flex items-center justify-center">
                  <FaGlobe className="h-4 w-4 text-cyan-600" />
                </div>
                <h3 className="font-semibold text-lg text-ink-primary">HTTP Request</h3>
              </div>
              <p className="text-ink-secondary mb-3">
                Makes an HTTP request to any external API. More flexible
                than the Webhook node -- you can use any method (GET, POST,
                PUT, PATCH, DELETE) and customize everything.
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-ink-primary mb-2">
                  Settings:
                </p>
                <ul className="text-sm text-ink-secondary space-y-1">
                  <li>
                    <strong>Method:</strong> GET, POST, PUT, PATCH, or
                    DELETE
                  </li>
                  <li>
                    <strong>URL:</strong> The API endpoint (supports{" "}
                    {"{{variables}}"})
                  </li>
                  <li>
                    <strong>Headers:</strong> JSON object with custom
                    headers
                  </li>
                  <li>
                    <strong>Body:</strong> JSON request body (for
                    POST/PUT/PATCH)
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Cold Outreach */}
        <section id="cold-outreach" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">
            Cold outreach campaigns
          </h2>
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <FaLightbulb className="h-6 w-6 text-teal-600 mt-1 flex-shrink-0" />
              <div>
                <p className="font-semibold text-teal-900 mb-1">
                  This section answers the most common question:
                </p>
                <p className="text-teal-800">
                  &quot;How do I send a message to a list of numbers, with
                  one new conversation every 30 minutes?&quot;
                </p>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-ink-primary mb-4">
            Step-by-step: Set up a cold outreach campaign
          </h3>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
                1
              </div>
              <div>
                <h4 className="font-semibold text-lg text-ink-primary mb-2">
                  Create a new flow
                </h4>
                <p className="text-ink-secondary">
                  Go to Flow Builder &gt; New Flow. Name it something like
                  &quot;Cold Outreach - April&quot;. Select your connected
                  WhatsApp session.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
                2
              </div>
              <div>
                <h4 className="font-semibold text-lg text-ink-primary mb-2">
                  Delete the default Trigger node
                </h4>
                <p className="text-ink-secondary">
                  For cold outreach, you do not need the &quot;Message
                  Received&quot; trigger because you are sending the first
                  message (not waiting for one). Click the trigger node and
                  delete it.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
                3
              </div>
              <div>
                <h4 className="font-semibold text-lg text-ink-primary mb-2">
                  Add a Loop node
                </h4>
                <p className="text-ink-secondary mb-3">
                  Click &quot;Loop&quot; in the toolbar and place it on the
                  canvas. Click it to open settings and configure:
                </p>
                <div className="bg-surface-card border border-surface-border rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-ink-primary">
                      Message
                    </p>
                    <p className="text-sm text-ink-muted">
                      Write your outreach message. Example: &quot;Hi! I came
                      across your business and wanted to reach out. We help
                      companies like yours automate their WhatsApp
                      communication. Would you be interested in a quick
                      chat?&quot;
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink-primary">
                      Recipients
                    </p>
                    <p className="text-sm text-ink-muted">
                      Paste your phone numbers (with country code, one per
                      line) or upload a CSV file. Example: 919876543210,
                      447911123456
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink-primary">
                      Interval
                    </p>
                    <p className="text-sm text-ink-muted">
                      Set to &quot;Simple&quot;, then enter{" "}
                      <strong>30</strong> and select{" "}
                      <strong>Minutes</strong>. This sends one message every
                      30 minutes.
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink-primary">
                      Repeat Count
                    </p>
                    <p className="text-sm text-ink-muted">
                      Set to <strong>0</strong> (unlimited) to message
                      everyone on the list, or enter the exact number of
                      messages you want to send.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
                4
              </div>
              <div>
                <h4 className="font-semibold text-lg text-ink-primary mb-2">
                  Save and activate
                </h4>
                <p className="text-ink-secondary">
                  Click <strong>Save Flow</strong>, go back to the flow
                  list, and toggle it to <strong>Active</strong>. Walytic
                  will start sending: one new conversation every 30 minutes
                  to each number on your list.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
                5
              </div>
              <div>
                <h4 className="font-semibold text-lg text-ink-primary mb-2">
                  Monitor results
                </h4>
                <p className="text-ink-secondary">
                  Open your flow and click the &quot;History&quot; button in
                  the top-right to see the execution log. Each message shows
                  its status: completed, running, or errored.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
              <FaExclamationTriangle className="h-5 w-5" /> Best practices
              for cold outreach
            </h4>
            <ul className="space-y-2 text-sm text-amber-800">
              <li>
                - Keep messages under 150 per session per day to avoid
                WhatsApp restrictions
              </li>
              <li>
                - Use intervals of 20-60 minutes between messages (not
                seconds)
              </li>
              <li>
                - Personalize your message instead of sending the same
                generic text to everyone
              </li>
              <li>
                - Start with small batches (20-50 numbers) to test your
                message before scaling up
              </li>
              <li>
                - Do not include suspicious links or spammy language
              </li>
              <li>
                - If contacts reply, respond promptly - active conversations
                build trust with WhatsApp
              </li>
            </ul>
          </div>
        </section>

        {/* Section: Auto-Reply Bot */}
        <section id="auto-reply-bot" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">
            Build an auto-reply bot
          </h2>
          <p className="text-ink-secondary mb-6">
            A simple bot that replies when someone sends a keyword. Here is
            a quick example:
          </p>

          <div className="bg-surface-card border border-surface-border rounded-xl p-6">
            <h3 className="font-semibold text-ink-primary mb-4">Example: Pricing bot</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center text-xs font-bold text-green-700">
                  1
                </div>
                <div className="flex-1 bg-green-50 rounded-lg p-3">
                  <p className="text-sm">
                    <strong>Message Received</strong> - Match: Contains,
                    Keyword: &quot;pricing&quot;
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <div className="h-6 w-0.5 bg-slate-300" />
              </div>
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700">
                  2
                </div>
                <div className="flex-1 bg-blue-50 rounded-lg p-3">
                  <p className="text-sm">
                    <strong>Send Message</strong> - &quot;Thanks for your
                    interest! Here are our plans: Starter $5/mo, Pro
                    $15/mo, Business $30/mo. Which one interests you?&quot;
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <div className="h-6 w-0.5 bg-slate-300" />
              </div>
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-orange-100 flex items-center justify-center text-xs font-bold text-orange-700">
                  3
                </div>
                <div className="flex-1 bg-orange-50 rounded-lg p-3">
                  <p className="text-sm">
                    <strong>Wait for Reply</strong> - Waits for the contact
                    to respond
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <div className="h-6 w-0.5 bg-slate-300" />
              </div>
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700">
                  4
                </div>
                <div className="flex-1 bg-purple-50 rounded-lg p-3">
                  <p className="text-sm">
                    <strong>Condition</strong> - Check if reply contains
                    &quot;starter&quot;, &quot;pro&quot;, or
                    &quot;business&quot;
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Variables */}
        <section id="variables" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">
            Using variables in messages
          </h2>
          <p className="text-ink-secondary mb-6">
            Variables let you personalize messages with contact data. Type{" "}
            {"{{ }}"} in any message field to open the variable picker.
          </p>

          <div className="bg-surface-card border border-surface-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-surface-border">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-ink-primary">
                    Variable
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-ink-primary">
                    What it inserts
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  ["{{firstName}}", "Contact's first name"],
                  ["{{lastName}}", "Contact's last name"],
                  ["{{name}}", "Full name"],
                  ["{{phone}}", "Phone number"],
                  ["{{email}}", "Email address"],
                  ["{{companyName}}", "Company name"],
                  ["{{tags}}", "All tags assigned to the contact"],
                  [
                    "{{stage}}",
                    "CRM stage (New, Contacted, Qualified, etc.)",
                  ],
                  [
                    "{{custom.fieldName}}",
                    "Any custom field you added to the lead",
                  ],
                  [
                    "{{prev.output}}",
                    "Output from the previous node in the flow",
                  ],
                ].map(([variable, desc]) => (
                  <tr key={variable}>
                    <td className="py-3 px-4 font-mono text-brand">
                      {variable}
                    </td>
                    <td className="py-3 px-4 text-ink-secondary">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Fallback values:</strong> Use{" "}
              <code className="bg-blue-100 px-1 rounded">
                {"{{firstName | there}}"}
              </code>{" "}
              to show &quot;there&quot; if the first name is empty. Result:
              &quot;Hi there&quot; instead of &quot;Hi &quot;.
            </p>
          </div>
        </section>

        {/* Section: Execution History */}
        <section id="execution-history" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">
            Checking execution history
          </h2>
          <p className="text-ink-secondary mb-4">
            Every flow run is logged. To see the history:
          </p>
          <ol className="space-y-3 text-ink-secondary mb-6">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold">
                1
              </span>
              <span>Open any flow in the canvas editor</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold">
                2
              </span>
              <span>
                Click the <strong>&quot;History&quot;</strong> button in the
                top-right corner
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold">
                3
              </span>
              <span>
                The panel shows the last 50 executions with their status
              </span>
            </li>
          </ol>
          <p className="text-ink-secondary mb-2">Each execution shows:</p>
          <ul className="space-y-1 text-ink-secondary text-sm">
            <li>
              - <strong>Contact number</strong> who triggered or received
              the flow
            </li>
            <li>
              - <strong>Status:</strong> completed (green), running (blue),
              waiting for reply (orange), errored (red)
            </li>
            <li>
              - <strong>Step-by-step log:</strong> expand any execution to
              see what each node did
            </li>
          </ul>
        </section>

        {/* Section: Tips */}
        <section id="tips" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-6">
            Tips and best practices
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                title: "Start simple",
                text: "Build a 2-3 node flow first. Test it. Then add more nodes.",
              },
              {
                title: "Save often",
                text: "Click Save Flow after every change. Unsaved changes are lost if you leave the page.",
              },
              {
                title: "Test before activating",
                text: "Send a test message to yourself first to make sure the flow works as expected.",
              },
              {
                title: "Use delays between messages",
                text: "Adding a 2-5 second delay between Send Message nodes makes conversations feel more natural.",
              },
              {
                title: "One trigger per keyword",
                text: "Avoid having two active flows with the same trigger keyword. Only the first match runs.",
              },
              {
                title: "Check the History panel",
                text: "If a flow is not working, the History panel shows exactly where it stopped and why.",
              },
            ].map((tip, i) => (
              <div
                key={i}
                className="bg-surface-card border border-surface-border rounded-lg p-4"
              >
                <h4 className="font-semibold text-sm text-ink-primary mb-1">{tip.title}</h4>
                <p className="text-sm text-ink-secondary">{tip.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Next guide */}
        <div className="flex items-center justify-between border-t border-surface-border pt-8">
          <Link
            to="/guide"
            className="text-sm text-ink-muted hover:text-ink-primary flex items-center gap-1"
          >
            <FaArrowLeft className="h-4 w-4" /> All guides
          </Link>
          <Link
            to="/guide/ai-features"
            className="text-sm font-medium text-brand hover:opacity-80 flex items-center gap-1"
          >
            Next: AI Chatbot & Voice <FaArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
